import { TZDate } from "@date-fns/tz";
import { isEventPassSoldOut } from "@/lib/availability";
import { toLocalDateKey } from "@/lib/dates/local-date";
import { APP_DEFAULT_TIME_ZONE } from "@/lib/dates/zoned-input";
import { isTicketVisibleOnEvent } from "@/lib/events/guest-list-visibility";
import type { MediaAsset } from "@/lib/media";

export interface OccurrencePass {
  occurrencePassId: number;
  passTypeId: number;
  name: string;
  slug: string;
  description: string | null;
  attendeesPerPass: number;
  priceAmountMinor: number;
  discoveryPriceAmountMinor?: number;
  isDeposit: boolean;
  totalPriceMinor: number | null;
  currency: string;
  capacity: number | null;
  reserved: number;
  remaining: number | null;
  isActive: boolean;
  /** Guest-list only; never the public price unless every pass is. */
  inviteOnly?: boolean;
  linkOnly?: boolean;
  hideFromNonGuests?: boolean;
  guestListEligible?: boolean | null;
  waitlistEnabled: boolean;
  waitlistState: {
    entryId: number;
    status: "active" | "offered";
    offerExpiresAt: string | null;
  } | null;
  paymentCollection?: "online" | "at_venue";
  saleStartsAt: string | null;
  saleEndsAt: string | null;
  maxPerBooking: number | null;
}

export interface BookingOccurrence {
  id: number;
  startTime: string | Date;
  endTime: string | Date;
  slotLabel: string | null;
  timezone: string | null;
  capacity: number | null;
  status: string;
  bookingOpensAt: string | Date | null;
  bookingClosesAt: string | Date | null;
  attendeeCount: number;
  reserved: number;
  remaining: number | null;
  timelineItems: Array<{
    id: number;
    kind: string;
    label: string;
    description: string | null;
    occursAt: string | Date;
    sortOrder: number;
  }>;
  passes: OccurrencePass[];
}

export type NormalizedOccurrence = Omit<
  BookingOccurrence,
  "bookingOpensAt" | "bookingClosesAt" | "timelineItems"
> & {
  start: Date;
  end: Date;
  bookingOpensAt: Date | null;
  bookingClosesAt: Date | null;
  timelineItems: Array<{
    id: number;
    kind: string;
    label: string;
    description: string | null;
    occursAt: Date;
    sortOrder: number;
  }>;
};

export interface HiiveSummary {
  id: number;
  name: string;
  slug: string;
  logoImage: MediaAsset | null;
  description?: string | null;
}

export const toDate = (value: Date | string | null | undefined): Date | null =>
  value ? new TZDate(new Date(value), APP_DEFAULT_TIME_ZONE) : null;

export const normalizeOccurrences = (
  occurrences: BookingOccurrence[],
): NormalizedOccurrence[] => {
  if (!Array.isArray(occurrences)) return [];
  try {
    return occurrences
      .filter(
        (occurrence) =>
          occurrence != null &&
          occurrence.startTime != null &&
          occurrence.endTime != null,
      )
      .map((occurrence) => {
        const start = toDate(occurrence?.startTime);
        const end = toDate(occurrence?.endTime);

        if (
          !start ||
          !end ||
          Number.isNaN(start.valueOf()) ||
          Number.isNaN(end.valueOf())
        ) {
          return null;
        }

        const timelineItems = Array.isArray(occurrence.timelineItems)
          ? occurrence.timelineItems.filter(Boolean)
          : [];

        return {
          ...occurrence,
          start,
          end,
          bookingOpensAt: toDate(occurrence.bookingOpensAt),
          bookingClosesAt: toDate(occurrence.bookingClosesAt),
          timelineItems: timelineItems
            .map((item) => {
              const occursAt = toDate(item?.occursAt);
              if (!occursAt || Number.isNaN(occursAt.valueOf())) {
                return null;
              }
              return {
                ...item,
                occursAt,
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null),
        };
      })
      .filter((occurrence): occurrence is NormalizedOccurrence =>
        Boolean(occurrence),
      )
      .sort((first, second) => first.start.getTime() - second.start.getTime());
  } catch {
    return [];
  }
};

/**
 * A canceled session leaves the page once another session is still on. An
 * event that ran twice on one day and lost one of them should read as a
 * one-time event again, not keep a "Canceled" chip next to the real one. Only
 * when every session is canceled do they stay, so the page still has a date
 * to explain itself with.
 */
export const withoutCanceledSessions = (
  normalizedOccurrences: NormalizedOccurrence[],
) => {
  const live = normalizedOccurrences.filter(
    (occurrence) => occurrence.status !== "canceled",
  );
  return live.length > 0 ? live : normalizedOccurrences;
};

export const groupOccurrencesByDate = (
  normalizedOccurrences: NormalizedOccurrence[],
) => {
  const grouped = new Map<string, NormalizedOccurrence[]>();
  normalizedOccurrences.forEach((occurrence) => {
    if (!occurrence?.start || Number.isNaN(occurrence.start.valueOf())) return;
    try {
      const key = toLocalDateKey(occurrence.start);
      if (!grouped.has(key)) {
        grouped.set(key, [occurrence]);
      } else {
        grouped.get(key)?.push(occurrence);
      }
    } catch {
      // Ignore malformed dates.
    }
  });
  for (const list of grouped.values()) {
    list.sort((a, b) => {
      const aTime = a.start?.getTime() ?? 0;
      const bTime = b.start?.getTime() ?? 0;
      return aTime - bTime;
    });
  }
  return grouped;
};

export const getDateKeys = (
  occurrencesByDate: Map<string, NormalizedOccurrence[]>,
) => Array.from(occurrencesByDate.keys()).sort();

export const getSelectedDateKey = (
  dateParam: string | null,
  dateKeys: string[],
) => {
  if (dateParam && dateKeys.includes(dateParam)) {
    return dateParam;
  }
  return dateKeys[0] ?? null;
};

export const getOccurrencesForSelectedDate = (
  occurrencesByDate: Map<string, NormalizedOccurrence[]>,
  selectedDateKey: string | null,
) => {
  if (!selectedDateKey) return [];
  return occurrencesByDate.get(selectedDateKey) ?? [];
};

export const getSelectedOccurrence = (
  occurrencesForSelectedDate: NormalizedOccurrence[],
  slotParam: string | null,
) => {
  if (slotParam) {
    const found = occurrencesForSelectedDate.find(
      (occurrence) => occurrence.id.toString() === slotParam,
    );
    if (found) return found;
  }
  return occurrencesForSelectedDate[0] ?? null;
};

/**
 * The cheapest active pass across every bookable occurrence, and whether any
 * other one costs something else.
 *
 * `priceVaries` is what earns the word "onwards": on an event with one price
 * it reads as a hidden upcharge. It spans every occurrence, like the minimum
 * does, so a class that costs more on Saturdays still says "onwards" here
 * even when the card for one date does not.
 *
 * Sold-out passes are left out of both (see `passesQuotableForPrice`): once
 * the ₹1,250 tier is gone the page says "₹1,500", not "₹1,250 onwards". Only
 * when nothing at all is left does it fall back to every active pass.
 */
export const getLowestActivePass = (
  normalizedOccurrences: NormalizedOccurrence[],
): {
  amountMinor: number;
  currency: string;
  isDeposit: boolean;
  totalPriceMinor: number | null;
  priceVaries: boolean;
} | null => {
  const allActivePasses = normalizedOccurrences.flatMap((occurrence) =>
    occurrence.passes
      .filter((pass) => pass.isActive && isTicketVisibleOnEvent(pass))
      .map((pass) => ({ pass, occurrenceRemaining: occurrence.remaining })),
  );
  // A guest-list ticket is not the public price, unless it is the only kind.
  const publicPasses = allActivePasses.filter(({ pass }) => !pass.inviteOnly);
  const activePasses = publicPasses.length > 0 ? publicPasses : allActivePasses;
  const onSale = activePasses.filter(
    ({ pass, occurrenceRemaining }) =>
      !isEventPassSoldOut(pass, occurrenceRemaining),
  );
  const quotable = onSale.length > 0 ? onSale : activePasses;

  type Lowest = {
    amountMinor: number;
    currency: string;
    isDeposit: boolean;
    totalPriceMinor: number | null;
  };
  let result: Lowest | null = null as Lowest | null;
  const distinctPrices = new Set<string>();
  for (const { pass } of quotable) {
    const amountMinor = pass.discoveryPriceAmountMinor ?? pass.priceAmountMinor;
    distinctPrices.add(`${pass.currency}:${amountMinor}`);
    if (
      result === null ||
      amountMinor < result.amountMinor ||
      (amountMinor === result.amountMinor &&
        pass.currency === "INR" &&
        result.currency !== "INR")
    ) {
      result = {
        amountMinor,
        currency: pass.currency,
        isDeposit: pass.isDeposit,
        totalPriceMinor: pass.totalPriceMinor,
      };
    }
  }
  if (result === null) return null;
  return { ...result, priceVaries: distinctPrices.size > 1 };
};

export const isMultiDate = (pattern: string) =>
  pattern === "multi_date_single_slot" ||
  pattern === "multi_date_multi_slot" ||
  pattern === "multi_date";
